import { Grid } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { t } from 'i18next'

import Dialog from 'src/components/shared/ui/Dialog/Dialog'
import { InputField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import ProcedimientoForm from 'src/components/views/EscritosLexnet/components/shared/components/forms/ProcedimientoForm/ProcedimientoForm'
import {
  ProcedimientoFormInitialvalues,
  ProcedimientoFormValidations,
  ProcedimientoFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/procedimientoTypes'
import { CautelarOpciones, Orden, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'

const styles = {
  '& :not(.disabled) .MuiInputBase-input, & :not(.disabled) .MuiInputBase-root': {
    cursor: 'pointer'
  }
}

type Props = {
  isPiezaSeparada: boolean
  isProcedimientoOpened: boolean
  closeProcedimiento: () => void
  getProcedimientoButtons: () => JSX.Element
  formSubmit: (values: ProcedimientoFormValues) => void
  tipoMensaje?: TipoMensaje
  checkIfDisabledField: () => boolean
  openProcedimiento?: () => void
}

function ProcedimientoView({
  isPiezaSeparada,
  isProcedimientoOpened,
  closeProcedimiento,
  getProcedimientoButtons,
  formSubmit,
  tipoMensaje,
  checkIfDisabledField,
  openProcedimiento = () => null
}: Props): JSX.Element {
  const { values } = useFormikContext<ProcedimientoFormValues>()
  const datosCautelar = values[FormField.CAUTELAR]
  const [ordenOrigen] = useField<Orden>(
    `${FormField.ORGANO_ORIGEN}.${FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL}`
  )

  function requiredField() {
    return (
      tipoMensaje === TipoMensaje.ESCRITO ||
      datosCautelar === CautelarOpciones.SI ||
      tipoMensaje === TipoMensaje.INICIADOR_EJECUCION ||
      Boolean(tipoMensaje === TipoMensaje.INICIADOR_ASUNTO && ordenOrigen.value) ||
      tipoMensaje === TipoMensaje.PERSONACION
    )
  }

  function getOrganoJudicial() {
    if (tipoMensaje === TipoMensaje.ESCRITO) {
      return values[FormField.ORGANO_DESTINO]
        ? values[FormField.ORGANO_DESTINO][FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]
        : null
    }
    return values[FormField.ORGANO_ORIGEN]
      ? values[FormField.ORGANO_ORIGEN][FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]
      : null
  }

  function getOrden() {
    if (tipoMensaje === TipoMensaje.ESCRITO) {
      return values[FormField.ORGANO_DESTINO]
        ? values[FormField.ORGANO_DESTINO][FormField.ORGANO_DESTINO_ORDEN_JUDICIAL]
        : null
    }
    return values[FormField.ORGANO_ORIGEN]
      ? values[FormField.ORGANO_ORIGEN][FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL]
      : null
  }

  return (
    <>
      <FieldsetGroup
        legend={
          tipoMensaje === TipoMensaje.ESCRITO
            ? t('form.legend.procedimiento')
            : t('form.legend.procedimiento_origen')
        }
        buttons={getProcedimientoButtons()}
        isSecondaryType
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} onClick={openProcedimiento}>
            <InputField
              id={FormField.PROCEDIMIENTO_DESCRIPCION}
              label={t('form.field.procedimiento')}
              readOnly
              required={requiredField()}
              disabled={checkIfDisabledField()}
              sx={styles}
            />
          </Grid>
          <Grid item xs={6} sm={4} onClick={openProcedimiento}>
            <InputField
              id={FormField.PROCEDIMIENTO_REFERENCIA}
              label={t('form.field.procedimiento_numero')}
              readOnly
              required={requiredField()}
              disabled={checkIfDisabledField()}
              sx={styles}
            />
          </Grid>
        </Grid>
        {isPiezaSeparada && (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} onClick={openProcedimiento}>
              <InputField
                id={FormField.PROCEDIMIENTO_ORIGEN}
                label={t('form.field.procedimiento_origen')}
                readOnly
                required={requiredField()}
                sx={styles}
                disabled={checkIfDisabledField()}
              />
            </Grid>
            <Grid item xs={6} sm={4} onClick={openProcedimiento}>
              <InputField
                id={FormField.PROCEDIMIENTO_ORIGEN_REFERENCIA}
                label={t('form.field.procedimiento_numero_pieza_origen')}
                readOnly
                required={requiredField()}
                sx={styles}
                disabled={checkIfDisabledField()}
              />
            </Grid>
            <Grid item xs={6} sm={4} onClick={openProcedimiento}>
              <InputField
                id={FormField.PROCEDIMIENTO_NUMERO_PIEZA}
                label={t('form.field.procedimiento_pieza')}
                readOnly
                sx={styles}
                disabled={checkIfDisabledField()}
              />
            </Grid>
          </Grid>
        )}
      </FieldsetGroup>
      <Dialog
        title={t('form.dialog.title.procedimiento')}
        isOpen={isProcedimientoOpened}
        close={closeProcedimiento}
        accept={() => null}
        cancel={closeProcedimiento}
        withForm
        validateFormik={false}
        formInitialValues={values[FormField.PROCEDIMIENTO] || ProcedimientoFormInitialvalues}
        formValidationScheme={ProcedimientoFormValidations}
        formHandleSubmit={formSubmit}
      >
        <ProcedimientoForm organoJudicial={getOrganoJudicial()} orden={getOrden()} />
      </Dialog>
    </>
  )
}

export default ProcedimientoView
